import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Box, Stack, Text, VisuallyHidden } from '@mantine/core';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { WizardStepTransition } from '../components/WizardStepTransition';
import { useHeroTextColours } from '../heroTextColours';

type ConfirmationScreenProps = {
  email: string;
};

export function ConfirmationScreen({ email }: ConfirmationScreenProps) {
  return (
    <AuthPageLayout>
      <Box pos="relative" w="100%" maw={720} px="md" py={80} mx="auto">
        <AmbientGlow />
        <WizardStepTransition stepKey="confirmation" direction={1} initial={true}>
          <ConfirmationContent email={email} />
        </WizardStepTransition>
      </Box>
    </AuthPageLayout>
  );
}

function AmbientGlow() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      aria-hidden
      initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={shouldReduceMotion ? { duration: 0 } : { duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: 'absolute',
        zIndex: -1,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 480,
        height: 480,
        borderRadius: '50%',
        background: 'radial-gradient(circle, var(--mantine-color-brand-1) 0%, transparent 70%)',
        filter: 'blur(48px)',
        pointerEvents: 'none',
      }}
    />
  );
}

const typewriterContainer = {
  initial: { opacity: 1 },
  animate: (delay: number) => ({
    opacity: 1,
    transition: {
      delayChildren: delay,
      staggerChildren: 0.03,
    },
  }),
};

const typewriterChar = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};

function AnimatedText({ text }: { text: string }) {
  const words = text.split(' ');
  return (
    <>
      {words.map((word, wordIndex) => (
        <React.Fragment key={wordIndex}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {word.split('').map((char, charIndex) => (
              <motion.span
                key={charIndex}
                variants={typewriterChar}
                style={{ display: 'inline-block' }}
              >
                {char}
              </motion.span>
            ))}
          </span>
          {wordIndex < words.length - 1 && ' '}
        </React.Fragment>
      ))}
    </>
  );
}

const heroParagraphStyle = {
  fontSize: 'clamp(1.5rem, 4vw, 3rem)',
  lineHeight: 1.25,
  letterSpacing: '-0.01em',
  maxWidth: 640,
};

function ConfirmationContent({ email }: { email: string }) {
  const shouldReduceMotion = useReducedMotion();
  const initial = shouldReduceMotion ? undefined : 'initial';
  const animate = shouldReduceMotion ? undefined : 'animate';
  const colours = useHeroTextColours();
  const title = "Thanks, we're almost there.";

  return (
    <div>
      <VisuallyHidden component="h1">{title}</VisuallyHidden>
      <motion.div initial={initial} animate={animate}>
        <Stack gap="xl" align="center" ta="center">
          <motion.div>
            <Stack gap={32} align="center">
              <motion.div
                custom={0.2}
                variants={typewriterContainer}
                initial={initial}
                animate={animate}
              >
                <Text
                  fw={FONT_WEIGHTS.bold}
                  c={colours.title}
                  style={heroParagraphStyle}
                  maw={800}
                  aria-hidden
                >
                  <AnimatedText text={title} />
                </Text>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <Text
                  fw={FONT_WEIGHTS.bold}
                  c={colours.accent}
                  style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)' }}
                >
                  Email sent. Check your inbox.
                </Text>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 2.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <Stack gap="md" align="center" maw={500}>
                  <Text size="md" c={colours.body} ta="center">
                    We&apos;ve sent a confirmation email to <b>{email}</b>. Please click the link to
                    confirm your email address and set your password.
                  </Text>
                  <Text size="md" c={colours.body} ta="center">
                    Once that&apos;s done you&apos;ll be able to log in to Afternoon and get
                    started.
                  </Text>
                </Stack>
              </motion.div>
            </Stack>
          </motion.div>
        </Stack>
      </motion.div>
    </div>
  );
}
